import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../../core/modules/global';
import { UPDATE_PRICERANGE_DIALOG } from '../../../../../core/constants/dialog';
import Spinner from '../../../Spinner';
import { useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import Swal from 'sweetalert2';
import { PutPriceRangePayloadRequest } from '../../../../../core/modules/master/interfaces/put-price-range';

const UpdateDialog = () => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { priceRange, putPriceRange, deletePriceRange, getPriceRanges, setPriceRange, priceRanges } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);

  const schema = yup.object().shape({
    start_price: yup
      .number()
      .min(0, 'El precio mínimo es 0')
      .required('Campo requerido')
      .typeError('Formato incorrecto'),
    end_price: yup
      .number()
      .required('Campo requerido')
      .typeError('Formato incorrecto')
      .when('start_price', (end_price, schema) =>
        schema.moreThan(yup.ref('start_price'), 'El precio final debe ser mayor que el precio inicial')
      ),
    description: yup.string().required('Campo requerido'),
  });

  const { control, formState, handleSubmit, resetField, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  // Watch form values for real-time validation
  const watchedValues = useWatch({ control });
  
  // Check for duplicates in real-time (excluding current record)
  const duplicateError = useMemo(() => {
    if (!watchedValues.start_price || !watchedValues.end_price || !priceRange) return null;
    
    const isDuplicate = priceRanges.some(range => 
      range.id !== priceRange.id && // Exclude current record being edited
      range.start_price === watchedValues.start_price && 
      range.end_price === watchedValues.end_price
    );
    
    return isDuplicate ? 'Ya existe un rango de precios con estos valores' : null;
  }, [watchedValues.start_price, watchedValues.end_price, priceRanges, priceRange]);

  const resetForm = useCallback(() => {
    resetField('start_price');
    resetField('end_price');
    resetField('description');
    setPriceRange(null);
    closeDialog(UPDATE_PRICERANGE_DIALOG);
  }, [closeDialog, resetField, setPriceRange]);

  useEffect(() => {
    if (priceRange) {
      setValue("start_price", priceRange.start_price);
      setValue("end_price", priceRange.end_price);
      setValue("description", priceRange.description);
    }
  }, [priceRange, setValue]);

  const onSubmit = useCallback(
    (values: PutPriceRangePayloadRequest) => {
      if (companyId && priceRange) {
        // Validar duplicados antes de enviar (excluyendo el registro actual)
        const isDuplicate = priceRanges.some(range => 
          range.id !== priceRange.id && // Exclude current record being edited
          range.start_price === values.start_price && 
          range.end_price === values.end_price
        );
        
        if (isDuplicate) {
          Swal.fire({
            title: "Error",
            text: "Ya existe un rango de precios con estos valores",
            icon: "error",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Aceptar",
          });
          return;
        }
        
        setLoading(true);
        putPriceRange(companyId, priceRange.id, values, () => {
          resetForm();
          getPriceRanges(companyId);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, priceRange, putPriceRange, resetForm, getPriceRanges, priceRanges]
  );

  const onDelete = useCallback(() => {
    Swal.fire({
      title: "Confimar acción",
      text: `Eliminar el rango ${priceRange?.description}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar!",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed && companyId && priceRange) {
        setLoading(true);

        deletePriceRange(
          companyId,
          priceRange.id,
          () => {
            setLoading(false);
            getPriceRanges(companyId);
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [companyId, deletePriceRange, getPriceRanges, resetForm, priceRange]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(UPDATE_PRICERANGE_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Modificar Rango de Precios
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="start_price"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Precio Inicio"
                          type="number"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.start_price}
                          helperText={errors?.start_price?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="end_price"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Precio Fin"
                          type="number"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.end_price}
                          helperText={errors?.end_price?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          type="textarea"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description}
                          helperText={errors?.description?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  {duplicateError && (
                    <div className="mb-24">
                      <Typography color="error" className="text-md text-center">
                        {duplicateError}
                      </Typography>
                    </div>
                  )}

                  <div className="flex justify-center gap-4">
                    <Button
                      variant="contained"
                      color="error"
                      sx={{ borderRadius: 50 }}
                      disabled={loading}
                      type="button"
                      onClick={onDelete}
                    >
                      <Spinner show={loading} />
                      Eliminar
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading || !!duplicateError}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDialog;
